// useBaseTypeAccess.js

import { useEffect, useMemo, useState } from 'react';

import useBaseAccess from 'hooks/useBaseAccess';
import {
  defaultTypeAccess,
  isSystemTypeData,
  resolveEffectiveTypeAccessLevel,
  typeAccessAllows,
} from 'security/typeAccess';
import { subscribeToMemberTypeAccess } from 'services/bases/BaseTypeAccessService';
import { useBase } from 'context/BaseContext';

export default function useBaseTypeAccess(baseOverride = null) {
  const { activeBase } = useBase();
  const {
    baseRole,
    baseTier,
    isCustomRole,
    roleMissing,
    roleDefinition,
    isRoleLoading,
  } = useBaseAccess(baseOverride);

  const effectiveBase = baseOverride || activeBase || null;
  const baseKey = effectiveBase?.key || null;

  const [memberTypeAccess, setMemberTypeAccess] = useState(defaultTypeAccess());
  const [isTypeAccessLoading, setIsTypeAccessLoading] = useState(true);

  useEffect(() => {
    if (!baseKey) {
      setMemberTypeAccess(defaultTypeAccess());
      setIsTypeAccessLoading(false);
      return undefined;
    }

    setIsTypeAccessLoading(true);
    return subscribeToMemberTypeAccess(
      baseKey,
      (next) => {
        setMemberTypeAccess(next || defaultTypeAccess());
        setIsTypeAccessLoading(false);
      },
      () => {
        setMemberTypeAccess(defaultTypeAccess());
        setIsTypeAccessLoading(false);
      }
    );
  }, [baseKey]);

  return useMemo(() => {
    const getTypeAccessLevel = (typeId, typeData = null) => resolveEffectiveTypeAccessLevel({
      tier: baseTier,
      roleKey: baseRole,
      isCustom: isCustomRole,
      roleDefinition,
      roleMissing,
      memberTypeAccess,
      typeId,
      isSystemType: isSystemTypeData(typeData),
    });

    return {
      memberTypeAccess,
      isTypeAccessLoading: isRoleLoading || isTypeAccessLoading,
      getTypeAccessLevel,
      canReadType: (typeId, typeData = null) => (
        typeAccessAllows(getTypeAccessLevel(typeId, typeData), 'read')
      ),
      canWriteType: (typeId, typeData = null) => (
        typeAccessAllows(getTypeAccessLevel(typeId, typeData), 'write')
      ),
    };
  }, [
    isCustomRole,
    isRoleLoading,
    isTypeAccessLoading,
    memberTypeAccess,
    roleDefinition,
    roleMissing,
    baseRole,
    baseTier,
  ]);
}
